/**
 * The music bed — a generative plainchant.
 *
 * The old bed was four detuned oscillators held forever, which is a drone, not
 * music. This is an actual chant engine, and every part of it is chosen to
 * sound like voices in a stone building rather than like a synthesiser:
 *
 *  Mode      Dorian on D — the first church mode, and the sound most people
 *            recognise as plainchant. Nothing here is transcribed from any
 *            recording or edition; the phrases are generated at runtime from
 *            the mode's own rules, so the music is original and there is
 *            nothing to license.
 *
 *  Organum   A second voice tracking the melody a fifth below, over a
 *            sustained D and A. Parallel fifths are the oldest western harmony
 *            and are what make a bare line sound sacred rather than thin.
 *
 *  Formants  Each voice is a sawtooth pushed through three band-pass filters
 *            tuned to the resonances of a human 'ah'. This is what separates a
 *            voice from a pad — a sine through a low-pass will always sound
 *            like a synthesiser, however slowly it moves.
 *
 *  Shape     Phrases follow the three parts of a psalm tone — a rise to the
 *            reciting note, several notes at that pitch, then a stepwise fall
 *            to the final. A random walk has no shape, and shape is the whole
 *            difference between a tune and ambience.
 *
 *  Bell      A struck bell on every cadence. Nothing else says "monastery" as
 *            fast, and it marks the musical structure out loud.
 *
 *  Room      A convolution reverb over a procedurally generated impulse — a
 *            long, dark, exponentially decaying tail. This is the single
 *            biggest contributor to it sounding good rather than cheap.
 */

/** Dorian on D, two octaves. The mode's final is D — phrases resolve there. */
const SCALE = [
  146.83, // D3
  164.81, // E3
  174.61, // F3
  196.0, // G3
  220.0, // A3
  246.94, // B3
  261.63, // C4
  293.66, // D4
  329.63, // E4
  349.23, // F4
  392.0, // G4
  440.0, // A4
];

/** 'ah' formants: the resonances that make a filtered saw read as a voice. */
const FORMANTS = [
  { frequency: 730, q: 9, gain: 1.0 },
  { frequency: 1090, q: 11, gain: 0.55 },
  { frequency: 2440, q: 13, gain: 0.18 },
];

/**
 * Loud enough to be music rather than atmosphere. The old value was a third of
 * this and the result was something you noticed only when it stopped — which is
 * the definition of ambience, not of a piece playing.
 *
 * Raised again after measuring it. At 0.2 the chant reached the speakers at
 * around -27 dBFS peak, which on a laptop at a normal volume setting is at or
 * below the noise floor of a quiet room: somebody pressed the speaker button
 * in the header, heard nothing, and reasonably concluded the sound was broken.
 *
 * Measured over a 45-second listen — one snapshot tells you nothing here,
 * because the music is generated and a peak reading depends entirely on
 * whether a phrase or a bell happened to fall inside the window. At 0.45 it
 * runs to -17.1 dBFS peak, -22.4 dBFS max RMS: clearly present, and still well
 * under the answer sounds at -9.7, so it stays a bed rather than a feature.
 */
const MASTER_GAIN = 0.45;

/**
 * A dark, decaying impulse response, generated rather than loaded.
 *
 * Noise shaped by an exponential decay is the standard cheap approximation of
 * a large room. The extra low-pass tilt over time is what stops it sounding
 * like a hiss: real stone absorbs high frequencies faster than low ones.
 */
function buildImpulse(ctx, seconds = 3.6, decay = 2.6) {
  const rate = ctx.sampleRate;
  const length = Math.floor(rate * seconds);
  const impulse = ctx.createBuffer(2, length, rate);

  for (let channel = 0; channel < 2; channel++) {
    const data = impulse.getChannelData(channel);
    let last = 0;
    for (let i = 0; i < length; i++) {
      const t = i / length;
      const envelope = (1 - t) ** decay;
      const white = Math.random() * 2 - 1;
      // One-pole low-pass that closes as the tail decays.
      last = last * (0.55 + 0.4 * t) + white * (0.45 - 0.4 * t);
      data[i] = last * envelope;
    }
  }
  return impulse;
}

export class ChantBed {
  constructor() {
    this.ctx = null;
    this.master = null;
    this.wet = null;
    this.voiceBus = null;
    this.droneNodes = [];
    this.timer = 0;
    this.level = 1;
    this.ducked = false;
    this.running = false;

  }

  get supported() {
    return typeof window !== 'undefined' && !!(window.AudioContext ?? window.webkitAudioContext);
  }

  /** Target gain given the user's level and whether narration is speaking. */
  #target() {
    return MASTER_GAIN * this.level * (this.ducked ? 0.42 : 1);
  }

  #build() {
    const AudioCtx = window.AudioContext ?? window.webkitAudioContext;
    if (!AudioCtx) return false;

    const ctx = this.ctx ?? new AudioCtx();
    this.ctx = ctx;

    this.master = ctx.createGain();
    this.master.gain.setValueAtTime(0.0001, ctx.currentTime);
    this.master.connect(ctx.destination);

    // Reverb runs in parallel with a little dry signal, so the chant sits in
    // the room rather than behind it.
    const convolver = ctx.createConvolver();
    convolver.buffer = buildImpulse(ctx);

    this.wet = ctx.createGain();
    this.wet.gain.value = 0.85;
    const dry = ctx.createGain();
    dry.gain.value = 0.32;

    this.voiceBus = ctx.createGain();
    this.voiceBus.gain.value = 1;

    // A gentle shelf keeps the sawtooths from ever getting brittle.
    const tone = ctx.createBiquadFilter();
    tone.type = 'lowpass';
    tone.frequency.value = 3200;
    tone.Q.value = 0.5;

    this.voiceBus.connect(tone);
    tone.connect(dry).connect(this.master);
    tone.connect(convolver).connect(this.wet).connect(this.master);

    return true;
  }

  /** One sung note: saw → formant bank → swell and release. */
  #sing(frequency, at, duration, gain = 1) {
    const ctx = this.ctx;

    const osc = ctx.createOscillator();
    osc.type = 'sawtooth';
    osc.frequency.setValueAtTime(frequency, at);

    // A slow, shallow vibrato that arrives after the note has settled — an
    // immediate vibrato sounds mechanical, a delayed one sounds sung.
    const vibrato = ctx.createOscillator();
    vibrato.frequency.setValueAtTime(4.6, at);
    const vibratoDepth = ctx.createGain();
    vibratoDepth.gain.setValueAtTime(0, at);
    vibratoDepth.gain.linearRampToValueAtTime(frequency * 0.004, at + duration * 0.45);
    vibrato.connect(vibratoDepth).connect(osc.frequency);

    const envelope = ctx.createGain();
    envelope.gain.setValueAtTime(0.0001, at);
    // Long swell in, longer release out. Chant has no attack transient.
    envelope.gain.exponentialRampToValueAtTime(gain, at + duration * 0.38);
    envelope.gain.exponentialRampToValueAtTime(0.0001, at + duration);

    let tail = osc;
    const created = [osc, vibrato, vibratoDepth, envelope];

    // Formant bank in parallel — each band gets its own share of the signal.
    const sum = ctx.createGain();
    sum.gain.value = 0.34;
    for (const formant of FORMANTS) {
      const band = ctx.createBiquadFilter();
      band.type = 'bandpass';
      band.frequency.setValueAtTime(formant.frequency, at);
      band.Q.setValueAtTime(formant.q, at);
      const bandGain = ctx.createGain();
      bandGain.gain.value = formant.gain;
      tail.connect(band).connect(bandGain).connect(sum);
      created.push(band, bandGain);
    }
    created.push(sum);

    sum.connect(envelope).connect(this.voiceBus);

    osc.start(at);
    osc.stop(at + duration + 0.1);
    vibrato.start(at);
    vibrato.stop(at + duration + 0.1);

    osc.onended = () => {
      for (const node of created) {
        try {
          node.disconnect();
        } catch {
          /* already gone */
        }
      }
    };
  }

  /** The sustained fifth underneath everything. */
  #startDrone() {
    const ctx = this.ctx;
    const now = ctx.currentTime;

    for (const [frequency, gain, detune] of [
      [73.42, 0.5, -3], // D2
      [110.0, 0.34, 2], // A2
      [146.83, 0.16, -1], // D3
    ]) {
      const osc = ctx.createOscillator();
      osc.type = 'triangle';
      osc.frequency.setValueAtTime(frequency, now);
      osc.detune.setValueAtTime(detune, now);

      const droneGain = ctx.createGain();
      droneGain.gain.setValueAtTime(gain * 0.42, now);

      // A very slow swell so the drone breathes rather than sitting flat.
      const breath = ctx.createOscillator();
      breath.frequency.setValueAtTime(0.035 + Math.random() * 0.02, now);
      const breathDepth = ctx.createGain();
      breathDepth.gain.setValueAtTime(gain * 0.14, now);
      breath.connect(breathDepth).connect(droneGain.gain);

      osc.connect(droneGain).connect(this.voiceBus);
      osc.start(now);
      breath.start(now);
      this.droneNodes.push(osc, breath, breathDepth, droneGain);
    }
  }

  /**
   * Build a whole phrase, rather than picking one note at a time.
   *
   * The old version was a weighted random walk, which is why it sounded like
   * ambience: a walk has no shape, and shape is what makes a line read as a
   * tune. Real chant is built from a psalm tone with three named parts, and
   * following that structure is most of what makes this sound composed:
   *
   *   intonation   a short rise from the final up to the reciting tone
   *   recitation   several notes at or around that tone — the words live here
   *   cadence      a stepwise fall back to the final, which closes the phrase
   *
   * Because every phrase ends where the next begins, consecutive phrases join
   * up instead of wandering.
   */
  #buildPhrase() {
    const FINAL = 0; // D3, the mode's home
    const TENOR = 4; // A3, the reciting tone a fifth above — chant's centre
    const notes = [];

    // Intonation: step up to the reciting tone.
    for (let i = 1; i <= TENOR; i += 2) notes.push({ degree: i, beats: 1 });

    // Recitation: hover at the tenor, with the odd neighbour above or below so
    // it is a sung line and not a held pitch.
    const recite = 3 + Math.floor(Math.random() * 4);
    for (let i = 0; i < recite; i++) {
      const roll = Math.random();
      notes.push({
        degree: roll < 0.62 ? TENOR : roll < 0.82 ? TENOR + 1 : TENOR - 1,
        beats: roll < 0.62 ? 1 : 0.75,
      });
    }

    // Occasional flourish — a rise above the tenor before turning back. Chant
    // does this at the middle of a verse, and it is the moment the line sounds
    // most like a melody.
    if (Math.random() < 0.45) {
      notes.push({ degree: TENOR + 2, beats: 1 });
      notes.push({ degree: TENOR + 1, beats: 0.75 });
    }

    // Cadence: walk down to the final and rest there.
    for (let d = TENOR - 1; d > FINAL; d--) notes.push({ degree: d, beats: 1 });
    notes.push({ degree: FINAL, beats: 2.6 });

    return notes;
  }

  /**
   * A struck bell, tolling at the close of a phrase.
   *
   * Nothing else in the mix says "monastery" as immediately, and because it
   * lands only on cadences it also marks the musical structure out loud.
   */
  #toll(at, frequency = 110) {
    const ctx = this.ctx;
    // Inharmonic partials — a bell is not a harmonic series, which is exactly
    // why it reads as metal rather than as another sung note.
    for (const [ratio, gain, decay] of [
      [1, 0.5, 5.5],
      [2.01, 0.28, 4],
      [2.99, 0.16, 3],
      [4.21, 0.09, 2.2],
      [5.43, 0.05, 1.6],
    ]) {
      const osc = ctx.createOscillator();
      osc.type = 'sine';
      osc.frequency.setValueAtTime(frequency * ratio, at);

      const env = ctx.createGain();
      env.gain.setValueAtTime(0.0001, at);
      env.gain.exponentialRampToValueAtTime(gain * 0.4, at + 0.008);
      env.gain.exponentialRampToValueAtTime(0.0001, at + decay);

      osc.connect(env).connect(this.voiceBus);
      osc.start(at);
      osc.stop(at + decay + 0.1);
      osc.onended = () => {
        try {
          osc.disconnect();
          env.disconnect();
        } catch {
          /* already gone */
        }
      };
    }
  }

  /**
   * Schedule one whole phrase, then a breath, then the next.
   *
   * Scheduling the phrase as a unit rather than a note at a time is what lets
   * the second voice line up with the first: organum is two voices moving in
   * parallel, and that only works if both know the whole line in advance.
   */
  #tick() {
    if (!this.running || !this.ctx) return;

    const ctx = this.ctx;
    const beat = 1.15; // seconds — slow, unmetered, the pace of sung prose
    let at = ctx.currentTime + 0.1;

    const phrase = this.#buildPhrase();
    for (let i = 0; i < phrase.length; i++) {
      const { degree, beats } = phrase[i];
      const duration = beats * beat;
      const last = i === phrase.length - 1;

      // The lead voice.
      this.#sing(SCALE[degree], at, duration, last ? 0.95 : 0.82);

      // Organum: a second voice a fifth below, the oldest western harmony.
      // Dropped on the final so the phrase closes in unison, the way it does
      // in the real thing.
      if (!last && degree - 4 >= 0) {
        this.#sing(SCALE[degree - 4], at + 0.02, duration, 0.34);
      }

      // Notes overlap slightly, so the line is legato rather than detached.
      at += duration * 0.92;
    }

    this.#toll(at - 0.4);

    const breath = 2.2 + Math.random() * 1.6;
    this.timer = setTimeout(() => this.#tick(), (at - ctx.currentTime + breath) * 1000);
  }

  async start() {
    if (this.running) return true;
    if (!this.ctx && !this.#build()) return false;

    try {
      await this.ctx.resume();
    } catch {
      /* resume can reject when the gesture wasn't trusted */
    }

    this.running = true;
    if (!this.droneNodes.length) this.#startDrone();

    const now = this.ctx.currentTime;
    this.master.gain.cancelScheduledValues(now);
    this.master.gain.setValueAtTime(Math.max(this.master.gain.value, 0.0001), now);
    this.master.gain.exponentialRampToValueAtTime(Math.max(this.#target(), 0.0001), now + 2.4);

    this.#tick();
    return true;
  }

  stop() {
    this.running = false;
    clearTimeout(this.timer);
    if (!this.ctx || !this.master) return;

    const now = this.ctx.currentTime;
    this.master.gain.cancelScheduledValues(now);
    this.master.gain.setValueAtTime(Math.max(this.master.gain.value, 0.0001), now);
    this.master.gain.exponentialRampToValueAtTime(0.0001, now + 1.4);

    // Let the reverb tail finish before tearing the drone down.
    setTimeout(() => {
      for (const node of this.droneNodes) {
        try {
          node.stop?.();
          node.disconnect();
        } catch {
          /* already stopped */
        }
      }
      this.droneNodes = [];
    }, 1800);
  }

  /** Fade to a new gain, used by both the level slider and ducking. */
  #fade(seconds) {
    if (!this.ctx || !this.master || !this.running) return;
    const now = this.ctx.currentTime;
    this.master.gain.cancelScheduledValues(now);
    this.master.gain.setValueAtTime(Math.max(this.master.gain.value, 0.0001), now);
    this.master.gain.exponentialRampToValueAtTime(Math.max(this.#target(), 0.0001), now + seconds);
  }

  setLevel(value) {
    this.level = Math.max(0, Math.min(1, value));
    this.#fade(0.15);
  }

  duck(on) {
    const next = !!on;
    if (this.ducked === next) return;
    this.ducked = next;
    this.#fade(0.8);
  }

  suspend() {
    this.ctx?.suspend?.();
  }

  resume() {
    if (this.running) this.ctx?.resume?.();
  }

  dispose() {
    this.stop();
    clearTimeout(this.timer);
    setTimeout(() => {
      this.ctx?.close?.().catch(() => {});
      this.ctx = null;
    }, 2000);
  }
}
